<div class="trd-page">
  <app-project-nav></app-project-nav>

  @if (loading()) {
    <div class="trd-state">
      <mat-progress-spinner mode="indeterminate" diameter="40"></mat-progress-spinner>
    </div>
  } @else if (loadError()) {
    <div class="trd-state trd-state--error tre-error">
      <span data-testid="results-load-error">{{ 'testRunResults.loadError' | translate }}</span>
      <a class="trd-back" data-testid="results-back" [routerLink]="executionsLink">
        {{ 'testRunResults.backToExecutions' | translate }}
        <mat-icon aria-hidden="true">chevron_right</mat-icon>
      </a>
    </div>
  } @else if (state(); as runnerState) {
    <div data-testid="run-results">
      <div class="trd-header">
        <div class="trd-heading">
          <div class="trd-eyebrow">{{ 'testRunResults.eyebrow' | translate }}</div>
          <div class="trd-title-row">
            <h1 class="trd-title">{{ 'testRunResults.title' | translate }}</h1>
            @if (runnerState.runKey) {
              <code class="trd-run-key" data-testid="results-run-key">{{ runnerState.runKey }}</code>
            }
            <span class="trd-art" [class.trd-art--auto]="automated()">
              <mat-icon aria-hidden="true">{{ automated() ? 'settings' : 'manage_accounts' }}</mat-icon>
              {{ (automated() ? 'testResults.detail.automated' : 'testResults.detail.manual') | translate }}
            </span>
          </div>
          <div class="trd-subtitle">
            @if (runnerState.runName) {
              {{ runnerState.runName }} ·
            }
            <span data-testid="results-execution-nr">
              {{ 'testRunResults.executionNr' | translate: { nr: runnerState.nr } }}
            </span>
          </div>
        </div>
        <div class="tre-head-links">
          @if (testResultLinkShown()) {
            <a class="trd-back" data-testid="results-open-test-result" [routerLink]="testResultLink">
              {{ 'testRunResults.openTestResult' | translate }}
              <mat-icon aria-hidden="true">photo_library</mat-icon>
            </a>
          }
          <a class="trd-back" data-testid="results-back" [routerLink]="executionsLink">
            {{ 'testRunResults.backToExecutions' | translate }}
            <mat-icon aria-hidden="true">chevron_right</mat-icon>
          </a>
        </div>
      </div>

      <div class="trd-card trd-summary">
        <div class="trd-summary-main">
          <div class="trd-result-circle trd-result-circle--{{ statusKey(outcome()) }}">
            <mat-icon aria-hidden="true">{{ outcomeIcon() }}</mat-icon>
          </div>
          <div>
            <span class="trd-pill trd-pill--{{ statusKey(outcome()) }}" data-testid="results-outcome">
              <span class="trd-pill-dot"></span>
              {{ outcomeLabel() | translate }}
            </span>
            <div class="trd-passed" data-testid="results-passed">
              {{
                'testResults.detail.summaryPassed'
                  | translate: { passed: runnerState.stepsPassed, total: runnerState.stepsTotal }
              }}
            </div>
          </div>
          <span class="trd-flex"></span>
          <div class="trd-metrics">
            <div class="trd-metric">
              <div class="trd-metric-label">{{ 'testResults.detail.metric.environment' | translate }}</div>
              <div class="trd-metric-value" data-testid="results-environment">{{ environment() }}</div>
            </div>
            <div class="trd-metric">
              <div class="trd-metric-label">{{ 'testResults.detail.metric.duration' | translate }}</div>
              <div class="trd-metric-value" data-testid="results-duration">{{ duration() }}</div>
            </div>
            <div class="trd-metric">
              <div class="trd-metric-label">{{ 'testResults.detail.metric.failed' | translate }}</div>
              <div class="trd-metric-value" data-testid="results-failed">{{ failed() }}</div>
            </div>
            <div class="trd-metric">
              <div class="trd-metric-label">{{ 'testResults.detail.metric.skipped' | translate }}</div>
              <div class="trd-metric-value" data-testid="results-skipped">{{ skipped() }}</div>
            </div>
          </div>
        </div>

        <div class="trd-meta">
          <span class="trd-flex"></span>
          <mat-icon class="trd-frozen-icon" aria-hidden="true">lock</mat-icon>
          <span class="trd-frozen">{{ 'testResults.detail.frozen' | translate }}</span>
        </div>

        <div class="trd-stats">
          <span class="trd-stats-label">{{ 'testResults.detail.stats.label' | translate }}</span>
          <span class="trd-stat trd-stat--total">
            {{ 'testResults.detail.stats.total' | translate }}
            <strong>{{ runnerState.stepsTotal }}</strong>
          </span>
          <span class="trd-stat trd-stat--pass">
            {{ 'testResults.detail.stats.pass' | translate }}
            <strong>{{ runnerState.stepsPassed }}</strong>
          </span>
          <span class="trd-stat trd-stat--fail">
            {{ 'testResults.detail.stats.fail' | translate }}
            <strong>{{ failed() }}</strong>
          </span>
          <span class="trd-stat trd-stat--blocked">
            {{ 'testResults.detail.stats.blocked' | translate }}
            <strong>{{ blocked() }}</strong>
          </span>
          <span class="trd-stat trd-stat--skip">
            {{ 'testResults.detail.stats.skip' | translate }}
            <strong>{{ skipped() }}</strong>
          </span>
        </div>
      </div>

      @if (runnerState.runnerJob; as job) {
        <app-runner-job-card [projectId]="projectId" [runnerJob]="job" [hasStepResults]="hasStepResults()" />
      }

      <div class="tre-steps-bar">
        <h2 class="trd-steps-heading">{{ 'testResults.detail.stepsHeading' | translate }}</h2>
        @if (configurationFilterShown()) {
          <app-filter-dropdown
            data-testid="konfig-filter"
            name="konfiguration"
            [placeholder]="'testRunResults.configurationFilter.all' | translate"
            [options]="configurationOptions()"
            [value]="configurationFilter()"
            (valueChange)="configurationFilter.set($event)"
          />
        }
      </div>

      @for (section of visibleSections(); track section.runnerCase.id) {
        <section class="trd-card tre-case" data-testid="case-section">
          <div class="tre-case-head">
            @if (section.runnerCase.key) {
              <code class="trd-run-key" data-testid="case-key">{{ section.runnerCase.key }}</code>
            }
            <h3 class="tre-case-title" data-testid="case-title" [title]="section.runnerCase.title">
              {{ section.runnerCase.title }}
            </h3>
            @if (section.runnerCase.configuration; as configuration) {
              <app-konfig-pill class="tre-case-config" [configuration]="configuration" />
            }
            @if (section.runnerCase.linkedBugKey) {
              <span class="tre-bug" data-testid="case-bug">
                🐞 {{ 'testRunResults.linkedBug' | translate: { key: section.runnerCase.linkedBugKey } }}
              </span>
            }
            <span class="trd-flex"></span>
            <span class="trd-pill trd-pill--{{ statusKey(section.runnerCase.status) }}" data-testid="case-pill">
              <span class="trd-pill-dot"></span>
              {{ 'testResults.result.' + statusKey(section.runnerCase.status) | translate }}
            </span>
          </div>

          <div class="trd-steps-head">
            <div class="trd-col-nr">{{ 'testResults.detail.table.number' | translate }}</div>
            <div class="trd-col-step">{{ 'testResults.detail.table.step' | translate }}</div>
            <div class="trd-col-duration">{{ 'testResults.detail.table.duration' | translate }}</div>
            <div class="trd-col-result">{{ 'testResults.detail.table.result' | translate }}</div>
          </div>

          @if (section.rows; as rows) {
            @for (row of rows; track row.item.step.id) {
              @if (isShown(row)) {
                @switch (row.kind) {
                  @case ('step') {
                    <ng-container
                      [ngTemplateOutlet]="stepRow"
                      [ngTemplateOutletContext]="{ $implicit: row.item, level: row.level }"
                    />
                  }
                  @case ('call') {
                    <div
                      class="trd-call trd-call--level-{{ row.level }}"
                      data-testid="call-skipped"
                      [attr.data-call-state]="row.item.step.callState"
                      [title]="lineOf(row.item.step)"
                    >
                      <div class="trd-col-nr"></div>
                      <div class="trd-col-step">
                        <span class="trd-call__module">{{ row.item.step.description }}</span>
                        –
                        <span data-testid="call-skipped-reason">{{ reasonOf(row.item.step) }}</span>
                      </div>
                      <div class="trd-col-duration"></div>
                      <div class="trd-col-result">
                        <span class="trd-pill trd-pill--{{ statusKey(row.item.step.actualStatus) }}">
                          <span class="trd-pill-dot"></span>
                          {{ 'testResults.result.' + statusKey(row.item.step.actualStatus) | translate }}
                        </span>
                      </div>
                    </div>
                  }
                  @case ('pass') {
                    <ng-container
                      [ngTemplateOutlet]="passHeader"
                      [ngTemplateOutletContext]="{ $implicit: row, testId: 'group-pass-' + row.pass }"
                    />
                  }
                  @case ('returnPass') {
                    <ng-container
                      [ngTemplateOutlet]="passHeader"
                      [ngTemplateOutletContext]="{ $implicit: row, testId: 'group-return-pass-' + row.pass }"
                    />
                  }
                  @case ('return') {
                    <ng-container
                      [ngTemplateOutlet]="passHeader"
                      [ngTemplateOutletContext]="{ $implicit: row, testId: 'group-return-block' }"
                    />
                  }
                  @default {
                    <div class="trd-group trd-group--level-{{ row.level }}" data-testid="step-group">
                      <mat-icon aria-hidden="true">content_copy</mat-icon>
                      <span class="trd-group__path" data-testid="group-path">
                        @for (part of headerOf(row).parts; track $index) {
                          <span>{{ part }}</span>
                          ›
                        }
                        <strong class="trd-group__path-last">{{ headerOf(row).last }}</strong>
                      </span>
                      @if (row.kind === 'loop') {
                        <span aria-hidden="true">·</span>
                        <span data-testid="group-rounds">
                          {{
                            (row.rounds === 1 ? 'runTree.rounds.one' : 'runTree.rounds.other')
                              | translate: { count: row.rounds }
                          }}
                        </span>
                      }
                      @if ((row.item.step.iterationIndex ?? null) !== null) {
                        <span data-testid="group-iteration">
                          {{ 'testResults.detail.iteration' | translate: { n: row.item.step.iterationIndex } }}
                        </span>
                      }
                    </div>
                  }
                }
              }
            } @empty {
              <ng-container [ngTemplateOutlet]="noSteps" />
            }
          } @else {
            @for (entry of section.entries; track entry.step.id) {
              @if (entry.isGroup) {
                <div class="trd-group" data-testid="step-group">
                  <mat-icon aria-hidden="true">content_copy</mat-icon>
                  <span>{{ entry.step.description }}</span>
                </div>
              } @else {
                <ng-container
                  [ngTemplateOutlet]="stepRow"
                  [ngTemplateOutletContext]="{ $implicit: entry, level: null }"
                />
              }
            } @empty {
              <ng-container [ngTemplateOutlet]="noSteps" />
            }
          }

          <ng-template #noSteps>
            <div class="trd-empty" data-testid="case-no-steps">{{ 'testRunResults.noSteps' | translate }}</div>
          </ng-template>

          <ng-template #stepRow let-entry let-level="level">
            <div class="trd-step" data-testid="step-row">
              <div
                class="trd-col-nr {{ level === null ? '' : 'trd-col-nr--level-' + level }}"
                [class.trd-col-nr--indent]="level === null && entry.depth > 0"
                data-testid="step-nr"
              >
                {{ entry.number }}
              </div>
              <div class="trd-col-step">
                <div class="trd-step-text" data-testid="step-text">{{ entry.step.description }}</div>
                @if (entry.step.expected) {
                  <div class="trd-soll" data-testid="step-expected">
                    <strong>{{ 'testResults.detail.soll' | translate }}</strong>
                    {{ entry.step.expected }}
                  </div>
                }
                @if (entry.step.actualResult) {
                  <div
                    class="trd-ist"
                    [class.trd-ist--fail]="entry.step.actualStatus === 'FAILED'"
                    data-testid="step-actual"
                  >
                    <strong>{{ 'testResults.detail.ist' | translate }}</strong>
                    {{ entry.step.actualResult }}
                  </div>
                }
                @if (entry.step.note) {
                  <div class="trd-comment" data-testid="step-note">
                    {{ 'testRunResults.note' | translate }} {{ entry.step.note }}
                  </div>
                }
                @if (entry.step.linkedBugKey) {
                  <div class="tre-bug tre-bug--step" data-testid="step-bug">
                    🐞 {{ 'testRunResults.linkedBug' | translate: { key: entry.step.linkedBugKey } }}
                  </div>
                }
              </div>
              <div class="trd-col-duration" data-testid="step-duration">{{ stepDuration(entry.step) }}</div>
              <div class="trd-col-result">
                <span class="trd-pill trd-pill--{{ statusKey(entry.step.actualStatus) }}" data-testid="step-pill">
                  <span class="trd-pill-dot"></span>
                  {{ 'testResults.result.' + statusKey(entry.step.actualStatus) | translate }}
                </span>
              </div>
            </div>
          </ng-template>

          <ng-template #passHeader let-row let-testId="testId">
            <div
              class="trd-group trd-group--level-{{ row.level }}"
              [class.trd-group--pass]="row.kind !== 'return'"
              [class.trd-group--return]="row.kind === 'return'"
              [attr.data-testid]="testId"
            >
              <button
                type="button"
                class="trd-group__toggle"
                [attr.aria-expanded]="!collapsed().has(row.item.step.id)"
                (click)="toggle(row.item.step.id)"
              >
                <mat-icon aria-hidden="true">
                  {{ collapsed().has(row.item.step.id) ? 'expand_more' : 'expand_less' }}
                </mat-icon>
                {{ passLabelOf(row) }}
              </button>
              @if (row.kind !== 'return') {
                <span
                  class="trd-pill trd-pill--{{ statusKey(row.item.step.actualStatus) }} trd-group-pill"
                  data-testid="group-status"
                >
                  <span class="trd-pill-dot"></span>
                  {{ 'testResults.result.' + statusKey(row.item.step.actualStatus) | translate }}
                </span>
              }
            </div>
          </ng-template>
        </section>
      } @empty {
        @if (configurationFilter()) {
          <div class="trd-card trd-empty" data-testid="konfig-filter-empty">
            {{ 'testRunResults.configurationFilter.empty' | translate }}
          </div>
        }
      }
    </div>
  }
</div>
